import { Link } from "react-router-dom";
import { Seo } from "../lib/seo";
import { FeatureTier } from "../components/FeatureTier";
import { DeploymentTopologyDiagram } from "../components/DeploymentTopologyDiagram";

const tiers = [
  {
    number: 1,
    title: "Production runtime",
    lede: "What you'd build yourself in nine months, hardened by a team whose only job is to do it well.",
    bullets: [
      { title: "Input validation & rate limits", text: ": every tool call schema-checked, per-customer quotas." },
      { title: "Prepare → execute idempotency", text: ": safe retries across the two-step write protocol." },
      { title: "Strict per-customer caching", text: ": response cache scoped so customers never see one another's data." },
      { title: "Circuit breakers", text: ": graceful degradation when a bank API or upstream model misbehaves." },
      { title: "Multi-region deployment", text: ": documented RPO and RTO targets per mode, validated by failover drills." },
      { title: "Capacity testing", text: ": load profiles published; pilot environments mirror production." },
    ],
  },
  {
    number: 2,
    title: "Identity, auth, and authorisation",
    lede: "The bank IdP stays canonical. Bancony Server speaks its language and the AI agent inherits the right scope, never more.",
    bullets: [
      { title: "Pre-built adapters", text: ": Entra ID, Okta, ForgeRock, Ping, custom bank auth APIs." },
      { title: "PSD2 / PSD3 SCA flows", text: ": step-up authentication wired into the agent protocol." },
      { title: "Per-tool / per-customer / per-channel scopes", text: ": no over-privileged calls." },
      { title: "Incremental scope consent", text: ": aligned to the 2026 MCP spec update." },
      { title: "Short-lived service credentials", text: ": no plaintext secrets, rotated automatically." },
      { title: "Audit-friendly", text: ": every authorisation decision logged with reason." },
    ],
  },
  {
    number: 3,
    title: "Observability and audit",
    lede: "The bank's CISO can answer 'what did the assistant do for this customer at 14:03 yesterday' in seconds.",
    bullets: [
      { title: "OpenTelemetry traces & metrics", text: ": per tool call, ready for the bank's existing observability stack." },
      { title: "Tamper-evident audit log", text: ": hash-chained, exportable, defensible." },
      { title: "Tool-aware dashboards", text: ": per-tool latency, error rate, and rejection reasons." },
      { title: "DORA Register-of-Information export", text: ": pre-formatted entries the bank's DPO can paste." },
      { title: "Incident classification hooks", text: ": aligned to ESA RTS criteria under DORA Article 17." },
      { title: "Customer-data audit", text: ": who looked at what, when, with the model+version that did it." },
    ],
  },
  {
    number: 4,
    title: "AI-native safety",
    lede: "The threat model is new. Bancony's controls are designed for it, not retrofitted to it. This is the tier you can't buy from a chatbot vendor.",
    bullets: [
      { title: "Signed & pinned tool manifests", text: ": tool poisoning attempts fail at load." },
      { title: "Response sanitisation", text: ": tool responses are wrapped, escaped, and tagged as untrusted before entering the model context." },
      { title: "Human-in-the-loop primitives", text: ": channel-appropriate confirmation UX for irreversible actions." },
      { title: "Declarative policy engine", text: ": describe what the agent can and can't do, audit it like any other policy." },
      { title: "Prompt-injection telemetry", text: ": anonymised attempts inform shared threat intelligence across Bancony customers." },
      { title: "EU AI Act Article 12-15 traceability", text: ": every model-driven action logged with the evidence a regulator expects." },
    ],
    highlight: true,
  },
  {
    number: 5,
    title: "Deployment and operational moat",
    lede: "The contract the bank's DPO needs to sign, plus the certifications, drills, and exit guarantees that back it.",
    bullets: [
      { title: "DORA Article 30 contract template", text: ": audit rights, sub-outsourcing register, data location, exit strategy." },
      { title: "Certification posture", text: ": SOC 2 Type II and ISO 27001 in scope; current status published honestly on /compliance." },
      { title: "Annual DORA self-assessment", text: ": published to customers, with remediations tracked." },
      { title: "SBOM and provenance", text: ": attested per release, signed." },
      { title: "Documented exit strategy", text: ": revert to plain bank2ai with no handler rewrite." },
      { title: "24/7 incident response", text: ": DORA-aligned SLAs, quarterly CISO threat briefings, bug bounty." },
    ],
  },
];

export const Server = () => (
  <>
    <Seo
      title="Bancony Server"
      description="A production server that connects your banking systems to AI agents. Compliant, hardened, supported, and built on an open standard."
      path="/server"
    />

    <section className="page-hero">
      <div className="page-hero-inner">
        <span className="section-eyebrow">Bancony Server</span>
        <h1>The production server that connects banking systems to AI agents.</h1>
        <p className="page-hero-sub">
          Bancony Server is the supported, hardened implementation of{" "}
          <a href="https://bank2ai.com" target="_blank" rel="noopener noreferrer" className="bank2ai-link" style={{ color: "var(--accent)" }}>bank2ai</a>,
          the open standard for letting AI agents safely call your bank's systems.
          Your team focuses on bank-specific handlers; we deliver the runtime,
          authorisation model, audit trail, and AI-native defences around them.
        </p>
        <div className="home-hero-ctas">
          <Link to="/contact?intent=server" className="btn btn-primary">Talk to us about implementation</Link>
          <a
            href="https://demo.bancony.com/#server"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-ghost"
          >
            Connect it to your AI tool
          </a>
          <a href="#tiers" className="btn btn-ghost">What's inside</a>
        </div>
      </div>
    </section>

    <section className="section soft">
      <div className="section-inner">
        <span className="section-eyebrow">What it enables</span>
        <h2>Banking, where the conversation is.</h2>
        <p className="lede" style={{ maxWidth: 760 }}>
          Your retail customers are starting to expect "just ask" experiences inside
          your own app. Your corporate customers, in the near future, will want to pull
          balances, reconcile, and authorise payments from inside the AI tools they
          already use for analysis: Claude, ChatGPT, and the rest.
        </p>
        <p className="lede" style={{ maxWidth: 760, marginTop: 0 }}>
          Bancony Server is the production-grade bridge that makes both possible. Any
          MCP-speaking AI client, retail or corporate, talks to one standard interface
          backed by your bank's controls, audit, and identity. You expose capabilities
          once; AI agents everywhere can use them.
        </p>
      </div>
    </section>

    <section className="section">
      <div className="section-inner">
        <span className="section-eyebrow">Why not just use the open library?</span>
        <h2>Three answers, one for each buyer in the room.</h2>

        <div className="differentiator-list three" style={{ marginTop: 32 }}>
          <div className="differentiator" style={{ background: "#fff", color: "var(--ink)", border: "1px solid var(--line)" }}>
            <h3 style={{ color: "var(--ink)" }}>For the CIO</h3>
            <p style={{ color: "var(--ink-soft)" }}>
              Time-to-production goes from quarters to weeks. The ongoing burden of patching, monitoring, and chasing spec drift moves off your team and onto ours. Your engineers ship bank logic; ours run the platform.
            </p>
          </div>
          <div className="differentiator" style={{ background: "#fff", color: "var(--ink)", border: "1px solid var(--line)" }}>
            <h3 style={{ color: "var(--ink)" }}>For the CISO</h3>
            <p style={{ color: "var(--ink-soft)" }}>
              Tool poisoning, indirect prompt injection, agent over-privilege. These are real new threat classes. Bank-grade defences are built in by people whose only job is to think about them.
            </p>
          </div>
          <div className="differentiator" style={{ background: "#fff", color: "var(--ink)", border: "1px solid var(--line)" }}>
            <h3 style={{ color: "var(--ink)" }}>For the DPO</h3>
            <p style={{ color: "var(--ink-soft)" }}>
              An Article 30 DORA contract counterparty. Audit rights, exit clauses, data residency, sub-outsourcing register. You cannot sign one of those with a GitHub repository.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="section soft" id="tiers">
      <div className="section-inner">
        <span className="section-eyebrow">What's in Bancony Server</span>
        <h2>Five tiers of capability.</h2>
        <p className="lede">
          The first three are what a senior platform team would eventually build. The
          fourth is the one that's hard to buy anywhere else. The fifth is the operational moat.
        </p>
        {tiers.map((t) => (
          <FeatureTier key={t.number} {...t} />
        ))}
      </div>
    </section>

    <section className="section">
      <div className="section-inner">
        <span className="section-eyebrow">Deployment topology</span>
        <h2>Three modes, one product.</h2>
        <p className="lede">
          On-prem inside the bank's datacentre, bank cloud inside the bank's own AWS or
          Azure tenancy, or Bancony SaaS in an EU region we operate. Pick the boundary
          that matches your risk appetite. Capability is identical across modes.
        </p>
        <DeploymentTopologyDiagram />
      </div>
    </section>

    <section className="section soft">
      <div className="section-inner">
        <span className="section-eyebrow">How it relates to bank2ai</span>
        <h2>A faithful implementation of an open spec.</h2>
        <p className="lede" style={{ maxWidth: 760 }}>
          Bancony Server passes the same drift tests as the open reference adapter. The
          schemas are unchanged. A bank can migrate to another bank2ai implementation,
          or back to the open library, with no rewrite of bank-specific handlers.{" "}
          <a href="https://bank2ai.com/docs/specification/overview" target="_blank" rel="noopener noreferrer" className="bank2ai-link">
            Read the spec on bank2ai.com
          </a>
          .
        </p>
      </div>
    </section>

    <section className="section">
      <div className="section-inner">
        <div className="final-cta">
          <div>
            <h2>Walk through Bancony Server with our team.</h2>
            <p>30 minutes. We'll show the runtime, the audit log, and the contract template.</p>
          </div>
          <div className="final-cta-cta">
            <Link to="/contact?intent=server" className="btn btn-primary">Talk to us about implementation</Link>
          </div>
        </div>
      </div>
    </section>
  </>
);
